/**
 * Portfolio Page
 * Aggregate view of all mining accounts with activity logs
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { PublicKey } from '@solana/web3.js';
import { useAccounts } from '../context/AccountContext';
import { useBalances } from '../context/BalanceContext';
import { authAPI, gameAPI, accountsAPI, AccountBalanceResponse } from '../api/client';
import { oreStatsFetch } from '../utils/oreStatsClient';
import { Header } from './Header';
import { Footer } from './Footer';
import { PortfolioTabSelector } from './portfolio/PortfolioTabSelector';
import { AggregateBalanceCard } from './portfolio/AggregateBalanceCard';
import { RoundHistoryTable } from './portfolio/RoundHistoryTable';
import { LifetimeStatsCard } from './portfolio/LifetimeStatsCard';
import { AggregateLifetimeStatsCard } from './portfolio/AggregateLifetimeStatsCard';
import { AllActivitiesLog } from './portfolio/AllActivitiesLog';
import { useTranslation } from '../context/LanguageContext';
import { SEO } from './SEO';
import '../win98.css';

// Evore program ID for PDA derivation
const EVORE_PROGRAM_ID = new PublicKey('8jaLKWLJAj5jVCZbxpe3zRUvLB3LD48MRtaQ2AjfCfxa');

// Animation variants for staggered panel entrance
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    }
  }
} as const;

const panelVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 25
    }
  }
};

/**
 * Convert bigint to 8-byte little-endian buffer
 */
function bigintToLeBytes(value: bigint): Buffer {
  const buffer = Buffer.alloc(8);
  buffer.writeBigUInt64LE(value);
  return buffer;
}

/**
 * Derive the ManagedMinerAuth PDA from a manager pubkey
 * This is the address used by ore-stats.com for Evore accounts
 */
function getManagedMinerAuthPda(managerPubkey: string): string {
  const manager = new PublicKey(managerPubkey);
  const authId = 0n; // Always authId=0 for each manager
  const [managedMinerAuth] = PublicKey.findProgramAddressSync(
    [
      Buffer.from('managed-miner-auth'),
      manager.toBuffer(),
      bigintToLeBytes(authId),
    ],
    EVORE_PROGRAM_ID
  );
  return managedMinerAuth.toBase58();
}

interface Prices {
  solUsd: number;
  oreUsd: number;
}

// Prefetched data types for instant tab switching
interface MinerStats {
  authority: string;
  round_id: number;
  deployed: number[];
  total_deployed: number;
  rewards_sol: number;
  rewards_ore: number;
  refined_ore: number;
  lifetime_rewards_sol: number;
  lifetime_rewards_ore: number;
  lifetime_deployed: number;
}

interface DeploymentEntry {
  round_id: number;
  miner_pubkey: string;
  square_id: number;
  amount: number;
  deployed_slot: number;
  sol_earned: number;
  ore_earned: number;
  is_winner: boolean;
  is_top_miner: boolean;
  winning_square: number;
}

interface PrefetchedAccountData {
  balance: AccountBalanceResponse | null;
  lifetimeStats: MinerStats | null;
  roundHistory: DeploymentEntry[] | null;
  minerAddress: string;
}

interface AllActivityItem {
  id: string;
  roundId: number | null;
  type: string;
  amount: string;
  claimedSol?: string | null;
  earnedSol?: string | null;
  earnedOre?: string | null;
  settledRoundId?: number | null;
  isTopMiner?: boolean;
  isMotherlodeWin?: boolean;
  motherlodeAmount?: number | null;
  deployedForSettledRound?: string | null;
  tilesCount?: number;
  txHash: string | null;
  success: boolean;
  error: string | null;
  timestamp: string;
  resultSource?: string | null;
  managerPubkey: string;
  accountName: string;
}

export function PortfolioPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authenticated, ready, getAccessToken, login, user } = usePrivy();
  const accountsContext = useAccounts();
  const { solBalance, oreBalance, storeBalance } = useBalances();

  // Stable ref for getAccessToken to avoid re-triggering effects on Privy token refresh
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Tab selection: 'all' for aggregate, or account id for individual
  const [selectedTab, setSelectedTab] = useState<string>('all');
  const [tabContentReady, setTabContentReady] = useState(true);

  // Handle tab change with delay
  const handleTabChange = useCallback((newTab: string) => {
    if (newTab === selectedTab) return;
    setTabContentReady(false);
    setSelectedTab(newTab);
    setTimeout(() => setTabContentReady(true), 200);
  }, [selectedTab]);

  // Minimized state for each panel
  const [minimizedPanels, setMinimizedPanels] = useState<Record<string, boolean>>({
    portfolioSummary: false,
    lifetimeStats: false,
    allActivities: false,
    individualBalance: false,
    individualLifetimeStats: false,
    roundHistory: false,
  });

  // Maximized state for panels that support it
  const [maximizedPanels, setMaximizedPanels] = useState<Record<string, boolean>>({
    allActivities: false,
    roundHistory: false,
  });

  const toggleMinimize = (panel: string) => {
    setMinimizedPanels(prev => ({
      ...prev,
      [panel]: !prev[panel]
    }));
  };

  const toggleMaximize = (panel: string) => {
    setMaximizedPanels(prev => ({
      ...prev,
      [panel]: !prev[panel]
    }));
  };

  // Get Privy wallet address for legacy accounts
  const privyWalletAddress = useMemo(() => {
    if (!user?.linkedAccounts) return null;
    const solanaWallet = user.linkedAccounts.find(
      (account: any) =>
        account.type === 'wallet' &&
        account.walletClientType === 'privy' &&
        account.chainType === 'solana'
    );
    return (solanaWallet as any)?.address || null;
  }, [user]);

  // All activity data for aggregate view
  const [allActivity, setAllActivity] = useState<AllActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Prices data
  const [prices, setPrices] = useState<Prices | null>(null);

  // Prefetched data for instant tab switching
  const [prefetchedData, setPrefetchedData] = useState<Record<string, PrefetchedAccountData>>({});
  const [prefetchComplete, setPrefetchComplete] = useState(false);

  // Legacy account stats fetched separately (like AggregateLifetimeStatsCard does)
  const [legacyAccountStats, setLegacyAccountStats] = useState<MinerStats | null>(null);

  // Leaderboard rank and top miner wins for overview
  const [leaderboardRank, setLeaderboardRank] = useState<number | null>(null);
  const [topMinerHits, setTopMinerHits] = useState(0);
  const [motherlodeHits, setMotherlodeHits] = useState(0);

  // Fetch all activity data
  const fetchAllActivity = useCallback(async () => {
    if (!authenticated) return;

    try {
      const token = await getAccessTokenRef.current();
      if (!token) return;

      const response = await gameAPI.getAllActivity(token);
      setAllActivity(response.activity || []);
      setPrices(response.prices || null);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch all activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  // Initial fetch
  useEffect(() => {
    if (authenticated && ready) {
      fetchAllActivity();
    }
  }, [authenticated, ready, fetchAllActivity]);

  // Refresh data periodically
  useEffect(() => {
    if (!authenticated) return;

    const interval = setInterval(fetchAllActivity, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [authenticated, fetchAllActivity]);

  // Fetch leaderboard rank for overview
  useEffect(() => {
    async function fetchRank() {
      try {
        const data = await gameAPI.getLeaderboard();
        const sorted = [...data.leaderboard].sort((a, b) => b.miningPnlUsd - a.miningPnlUsd);
        const userAddresses = new Set<string>();
        if (privyWalletAddress) userAddresses.add(privyWalletAddress);
        accountsContext.accounts.forEach(a => {
          if (a.oreMinerPda) userAddresses.add(a.oreMinerPda);
        });
        const idx = sorted.findIndex(e => e.isOwnEntry || userAddresses.has(e.wallet));
        setLeaderboardRank(idx >= 0 ? idx + 1 : null);
      } catch (err) {
        console.error('Failed to fetch leaderboard rank:', err);
      }
    }
    if (authenticated && ready) fetchRank();
  }, [authenticated, ready, privyWalletAddress, accountsContext.accounts]);

  // Fetch top miner and motherlode hit counts across all sub-accounts from ore-stats.com
  useEffect(() => {
    async function fetchOverviewHits() {
      try {
        const minerAddresses: string[] = [];
        if (privyWalletAddress) minerAddresses.push(privyWalletAddress);
        for (const account of accountsContext.accounts) {
          if (account.accountType !== 'legacy') {
            minerAddresses.push(getManagedMinerAuthPda(account.managerPubkey));
          }
        }

        let topMinerTotal = 0;
        let motherlodeTotal = 0;
        for (const addr of minerAddresses) {
          const [topMinerData, motherlodeData] = await Promise.all([
            oreStatsFetch<any>(`/history/miner/${addr}/deployments?winner_only=true&base_ore_only=true&round_limit=1000`),
            oreStatsFetch<any>(`/history/miner/${addr}/deployments?winner_only=true&motherlode_only=true&round_limit=1000`),
          ]);
          if (topMinerData) {
            const uniqueRounds = new Set((topMinerData.data || []).map((d: any) => d.round_id));
            topMinerTotal += uniqueRounds.size;
          }
          if (motherlodeData) {
            const uniqueRounds = new Set((motherlodeData.data || []).map((d: any) => d.round_id));
            motherlodeTotal += uniqueRounds.size;
          }
        }
        setTopMinerHits(topMinerTotal);
        setMotherlodeHits(motherlodeTotal);
      } catch (err) {
        console.error('Failed to fetch overview hits:', err);
      }
    }
    if (authenticated && ready && accountsContext.accounts.length > 0) fetchOverviewHits();
  }, [authenticated, ready, privyWalletAddress, accountsContext.accounts]);

  // Fetch legacy account stats separately (same pattern as AggregateLifetimeStatsCard)
  useEffect(() => {
    async function fetchLegacyStats() {
      if (!privyWalletAddress) {
        setLegacyAccountStats(null);
        return;
      }

      try {
        const data = await oreStatsFetch<any>(`/miner/${privyWalletAddress}`);
        setLegacyAccountStats(data);
      } catch (err) {
        console.error('Failed to fetch legacy account stats:', err);
        setLegacyAccountStats(null);
      }
    }

    fetchLegacyStats();
  }, [privyWalletAddress]);

  // Prefetch all individual account data on page load for instant tab switching
  // Staggered to avoid rate limits (429 errors)
  useEffect(() => {
    async function prefetchAllAccountData() {
      if (!authenticated || !ready || accountsContext.accounts.length === 0) return;

      // Reset prefetchComplete so pnlBreakdown useMemo recalculates when we finish
      setPrefetchComplete(false);

      try {
        const token = await getAccessTokenRef.current();
        if (!token) return;

        const dataMap: Record<string, PrefetchedAccountData> = {};
        const STAGGER_DELAY_MS = 150; // Delay between each account's requests

        // Process accounts sequentially with staggered delays
        for (let i = 0; i < accountsContext.accounts.length; i++) {
          const account = accountsContext.accounts[i];

          // Add delay before each account (except first)
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, STAGGER_DELAY_MS));
          }

          // Derive minerAddress for this account
          // For legacy accounts, use privyWalletAddress (same as master branch)
          // For Evore accounts, derive the ManagedMinerAuth PDA
          let minerAddr: string | null;
          if (account.accountType === 'legacy') {
            minerAddr = privyWalletAddress;
          } else {
            minerAddr = getManagedMinerAuthPda(account.managerPubkey);
          }

          // Skip fetching stats if no miner address available
          if (!minerAddr) {
            dataMap[account.id] = {
              balance: null,
              lifetimeStats: null,
              roundHistory: null,
              minerAddress: '',
            };
            setPrefetchedData({ ...dataMap });
            continue;
          }

          // Fetch all data in parallel for this single account
          // Legacy accounts can't use accountsAPI.getBalance (Evore-only endpoint)
          const balancePromise = account.accountType === 'legacy'
            ? Promise.all([
                authAPI.getStatus(token, account.managerPubkey) as Promise<any>,
                gameAPI.getMiner(token, account.managerPubkey) as Promise<any>,
              ]).then(([statusResp, minerData]) => ({
                balance: {
                  autodeployBalance: statusResp?.user?.walletBalance || 0,
                  pendingSolRewards: 0,
                  pendingOreRewards: minerData?.unrefinedOre || 0,
                  refinedOre: minerData?.refinedOre || 0,
                },
              } as AccountBalanceResponse))
            : accountsAPI.getBalance(token, account.id);

          const [balanceResult, statsData, historyData] = await Promise.allSettled([
            balancePromise,
            oreStatsFetch<any>(`/miner/${minerAddr}`),
            oreStatsFetch<any>(`/history/miner/${minerAddr}/deployments?round_limit=50`),
          ]);

          dataMap[account.id] = {
            balance: balanceResult.status === 'fulfilled' ? balanceResult.value : null,
            lifetimeStats: statsData.status === 'fulfilled' ? statsData.value : null,
            roundHistory: historyData.status === 'fulfilled' ? historyData.value?.data || null : null,
            minerAddress: minerAddr,
          };

          // Update state progressively so first tabs load faster
          setPrefetchedData({ ...dataMap });
        }

        setPrefetchComplete(true);
      } catch (err) {
        console.error('Failed to prefetch account data:', err);
        setPrefetchComplete(true); // Still mark complete so UI isn't stuck
      }
    }

    prefetchAllAccountData();
  }, [authenticated, ready, accountsContext.accounts, privyWalletAddress]);

  // Get selected account details
  const selectedAccount = selectedTab !== 'all'
    ? accountsContext.accounts.find(a => a.id === selectedTab)
    : null;

  // Memoize minerAddress to prevent unnecessary re-renders
  const minerAddress = useMemo(() => {
    if (!selectedAccount) return null;
    if (selectedAccount.accountType === 'legacy') {
      return privyWalletAddress;
    }
    return getManagedMinerAuthPda(selectedAccount.managerPubkey);
  }, [selectedAccount, privyWalletAddress]);

  // Compute PNL breakdown in parent to prevent child re-renders during prefetch
  // Only depends on prefetchComplete (not prefetchedData) to avoid re-renders during prefetch
  const LAMPORTS_PER_SOL = 1e9;
  const ORE_DECIMALS = 1e11;
  const PIE_COLORS = ['#5d62e1', '#fbe152', '#b65af1', '#0cafce', '#f4547b', '#f99e4c'];


  const pnlBreakdown = useMemo(() => {
    // Only compute when prefetch is complete - this prevents re-renders during prefetch
    if (!prefetchComplete || !prices) return [];

    return accountsContext.accounts.map((account, index) => {
      // Use legacyAccountStats for legacy accounts, prefetchedData for Evore accounts
      const stats = account.accountType === 'legacy'
        ? legacyAccountStats
        : prefetchedData[account.id]?.lifetimeStats;

      let lifetimeDeployed = 0;
      let lifetimeSolEarned = 0;
      let lifetimeOreEarned = 0;

      if (stats) {
        lifetimeDeployed = (stats.lifetime_deployed || 0) / LAMPORTS_PER_SOL;
        lifetimeSolEarned = (stats.lifetime_rewards_sol || 0) / LAMPORTS_PER_SOL;
        lifetimeOreEarned = (stats.lifetime_rewards_ore || 0) / ORE_DECIMALS;
      }

      const netSolCost = lifetimeDeployed - lifetimeSolEarned;
      const netSolCostUsd = netSolCost * prices.solUsd;
      const oreEarnedUsd = lifetimeOreEarned * prices.oreUsd;
      const accumulationPnl = oreEarnedUsd - netSolCostUsd;

      return {
        accountId: account.id,
        displayName: account.displayName || t('portfolio.page.accountFallback', { index: index + 1 }),
        pnlUsd: accumulationPnl,
        color: PIE_COLORS[index % PIE_COLORS.length],
        hasRounds: lifetimeDeployed > 0,
      };
    }).filter(item => item.hasRounds);
  }, [prefetchComplete, prices, accountsContext.accounts, prefetchedData, legacyAccountStats]);

  // Handle not authenticated
  if (ready && !authenticated) {
    return (
      <div className="win98-desktop">
        <div className="win98-landing-container">
          <Header onBackToHome={() => navigate('/')} />
          <div className="win98-window" style={{ maxWidth: '500px', margin: '20px auto' }}>
            <div className="win98-title-bar">
              <div className="win98-title">{t('portfolio.page.title')}</div>
            </div>
            <div className="win98-content win98-p-xl" style={{ textAlign: 'center' }}>
              <p className="win98-text-small win98-mb-lg">
                {t('portfolio.page.loginPrompt')}
              </p>
              <button
                className="win98-button win98-button-primary"
                onClick={login}
              >
                {t('portfolio.page.loginButton')}
              </button>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="win98-desktop">
      <SEO
        title="Portfolio — MineMore | Mining Performance"
        description="View your ORE mining portfolio, account balances, and lifetime performance stats."
        path="/portfolio"
        robots="noindex, nofollow"
      />
      <motion.div
        className="win98-landing-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Header onBackToHome={() => navigate('/')} />

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Portfolio Summary with Tab Selector */}
          <motion.div variants={panelVariants}>
          <AggregateBalanceCard
            accounts={accountsContext.accounts}
            prices={prices}
            getAccessToken={getAccessToken}
            minimized={minimizedPanels.portfolioSummary}
            onMinimize={() => toggleMinimize('portfolioSummary')}
            selectedTab={selectedTab}
            selectedAccount={selectedAccount}
            prefetchedBalance={selectedTab !== 'all' ? prefetchedData[selectedTab]?.balance : undefined}
            pnlBreakdown={pnlBreakdown}
            prefetchComplete={prefetchComplete}
            leaderboardRank={leaderboardRank}
            topMinerHits={topMinerHits}
            motherlodeHits={motherlodeHits}
            storeBalance={storeBalance}
          >
            <PortfolioTabSelector
              accounts={accountsContext.accounts}
              selectedTab={selectedTab}
              onSelectTab={handleTabChange}
            />
          </AggregateBalanceCard>
          </motion.div>

          {/* Content based on selected tab */}
          {!tabContentReady ? null : selectedTab === 'all' ? (
            /* Aggregate View */
            <>

              <AggregateLifetimeStatsCard
                accounts={accountsContext.accounts}
                prices={prices}
                privyWalletAddress={privyWalletAddress}
                minimized={minimizedPanels.lifetimeStats}
                onMinimize={() => toggleMinimize('lifetimeStats')}
              />

              <AllActivitiesLog
                activity={allActivity}
                prices={prices}
                loading={loading}
                error={error}
                onRefresh={fetchAllActivity}
                minimized={minimizedPanels.allActivities}
                onMinimize={() => toggleMinimize('allActivities')}
                maximized={maximizedPanels.allActivities}
                onMaximize={() => toggleMaximize('allActivities')}
              />
            </>
          ) : selectedAccount && (
            /* Individual Account View */
            <>
              {minerAddress && (
                <LifetimeStatsCard
                  minerAddress={minerAddress}
                  prices={prices}
                  minimized={minimizedPanels.individualLifetimeStats}
                  onMinimize={() => toggleMinimize('individualLifetimeStats')}
                  prefetchedStats={selectedAccount?.accountType === 'legacy' ? undefined : prefetchedData[selectedTab]?.lifetimeStats}
                />
              )}

              {minerAddress && (
                <RoundHistoryTable
                  minerAddress={minerAddress}
                  minimized={minimizedPanels.roundHistory}
                  onMinimize={() => toggleMinimize('roundHistory')}
                  maximized={maximizedPanels.roundHistory}
                  onMaximize={() => toggleMaximize('roundHistory')}
                  prefetchedHistory={selectedAccount?.accountType === 'legacy' ? undefined : prefetchedData[selectedTab]?.roundHistory}
                />
              )}
            </>
          )}
        </div>

        <Footer />
      </motion.div>
    </div>
  );
}
